import { useState } from "react";
import axios from "axios";
import { ShieldCheck, Lock, HeartHandshake, Repeat, DollarSign, Building2, Home as HomeIcon, Package, Landmark, Briefcase, Smartphone, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [25, 50, 100, 250];

const waysToGive = [
  { icon: DollarSign, title: "One-Time Gift", desc: "A single secure card donation in any amount." },
  { icon: Repeat, title: "Monthly Giving", desc: "Become a sustaining partner with a recurring monthly gift." },
  { icon: Building2, title: "Corporate Sponsorship", desc: "Underwrite a program or drive under your company's name." },
  { icon: Package, title: "In-Kind Donations", desc: "Supplies, essentials, and resources for direct community relief." },
  { icon: HomeIcon, title: "Property & Vehicle", desc: "Real estate or vehicle gifts that expand our capacity." },
  { icon: Landmark, title: "Planned Giving", desc: "Include the Foundation in your legacy and estate plans." },
  { icon: Briefcase, title: "Employer Match", desc: "Double your impact through your employer's matching program." },
  { icon: Smartphone, title: "Venmo", desc: "Send a gift on Venmo to @TandemTFoundation." },
  { icon: Smartphone, title: "Cash App", desc: "Give on Cash App to $TandemTFoundation any time." },
  { icon: Smartphone, title: "Zelle", desc: "Send directly through Zelle to tandemtfoundation." },
  { icon: Smartphone, title: "PayPal", desc: "Give securely via PayPal to Tandem T Foundation." },
  { icon: HeartHandshake, title: "PayPal Giving Fund", desc: "Donate through our official PayPal Giving Fund charity profile.", link: "https://www.paypal.com/US/fundraiser/charity/6244251", cta: "Give Through PayPal Giving Fund" },
];

export default function Donate() {
  useSEO({
    title: "Donate — The Tandem T Foundation",
    description: "Make a secure one-time or monthly gift to The Tandem T Foundation and help mothers, children, and youth build a new beginning.",
    path: "/foundation/donate",
  });

  const [frequency, setFrequency] = useState("once");
  const [preset, setPreset] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const usingCustom = custom !== "";
  const effectiveAmount = usingCustom ? parseFloat(custom) : preset;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!effectiveAmount || effectiveAmount < 1) {
      setError("Please enter a donation amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        frequency,
        origin_url: window.location.origin,
        donor_name: name || undefined,
        donor_email: email || undefined,
      };
      if (usingCustom) payload.custom_amount = effectiveAmount;
      else payload.preset = preset;
      const { data } = await axios.post(`${API}/donations/checkout`, payload);
      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong starting your donation.";
      setError(typeof msg === "string" ? msg : "Please check your amount and try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <PageHero
        overline="Support the Mission"
        title={<>Give a <em className="italic">new beginning.</em></>}
        subtitle="Your gift supports all three programs — New Beginnings™ for women and children rebuilding after domestic violence, UNSPOKEN™ for men's mental health and wellness, and Next Move™ for youth and family experiences. Every gift carries one message: You Matter. Give once, or become a monthly sustaining partner."
        testId="donate-hero"
      />

      <section className="py-20 md:py-28 border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Giving form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="donation-form" className="border border-white/15 p-8 md:p-10 bg-white/[0.02]">
                {/* Frequency toggle */}
                <div className="grid grid-cols-2 gap-2 p-1 border border-white/15">
                  <button
                    type="button"
                    data-testid="freq-once"
                    onClick={() => setFrequency("once")}
                    className={`overline py-3 transition-colors ${frequency === "once" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                  >
                    One-Time
                  </button>
                  <button
                    type="button"
                    data-testid="freq-monthly"
                    onClick={() => setFrequency("monthly")}
                    className={`overline py-3 transition-colors ${frequency === "monthly" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                  >
                    Monthly
                  </button>
                </div>

                {/* Presets */}
                <p className="overline text-white/50 mt-8 mb-4">Choose an amount</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`preset-${p}`}
                      onClick={() => { setPreset(p); setCustom(""); }}
                      className={`py-5 font-serif text-2xl font-light border transition-colors ${!usingCustom && preset === p ? "bg-white text-black border-white" : "border-white/20 text-white hover:border-white/50"}`}
                    >
                      ${p}
                    </button>
                  ))}
                </div>

                {/* Custom */}
                <p className="overline text-white/50 mt-8 mb-3">Or enter a custom amount</p>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-serif text-xl">$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="decimal"
                    data-testid="custom-amount"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Custom amount"
                    className="w-full bg-transparent border border-white/20 focus:border-white outline-none py-4 pl-10 pr-4 text-white font-serif text-xl placeholder:text-white/30"
                  />
                </div>

                {/* Donor details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                  <div>
                    <label className="overline text-white/50 block mb-2">Name (optional)</label>
                    <input
                      type="text"
                      data-testid="donor-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="overline text-white/50 block mb-2">Email (for receipt)</label>
                    <input
                      type="email"
                      data-testid="donor-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-2 text-white"
                    />
                  </div>
                </div>

                {error && <p data-testid="donate-error" className="text-sm text-white/90 mt-6 border border-white/25 px-4 py-3">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="donate-submit"
                  className="group w-full mt-8 overline px-6 py-5 bg-white text-black hover:bg-white/85 transition-colors disabled:opacity-60"
                >
                  {loading ? "Redirecting to secure checkout…" : `Give ${usingCustom && effectiveAmount ? `$${effectiveAmount}` : `$${preset}`}${frequency === "monthly" ? " / month" : ""}`}
                </button>

                <div className="flex items-center justify-center gap-2 mt-5 text-white/45 text-xs">
                  <Lock size={12} /> Secure checkout powered by Stripe
                </div>
              </form>
            </Reveal>
          </div>

          {/* Trust column */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-white/15 p-8 bg-white/[0.02]">
                <p className="overline text-white/50">Your gift, protected</p>
                <ul className="mt-6 space-y-6 text-sm">
                  <li className="flex items-start gap-4">
                    <ShieldCheck size={18} className="mt-0.5 shrink-0 text-white" />
                    <span className="text-white/75 font-light">Held in dedicated Foundation accounts, legally separate from any for-profit entity.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <Lock size={18} className="mt-0.5 shrink-0 text-white" />
                    <span className="text-white/75 font-light">Payments are processed securely by Stripe. We never see or store your card details.</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <HeartHandshake size={18} className="mt-0.5 shrink-0 text-white" />
                    <span className="text-white/75 font-light">Every dollar advances our charitable mission and programs.</span>
                  </li>
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="text-xs text-white/45 font-light leading-relaxed">
                    Tandem T Foundation is a federally recognized 501(c)(3) nonprofit organization.
                    Contributions are tax-deductible to the extent permitted by law.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Give Instantly — QR codes */}
      <section id="give-instantly" className="py-20 md:py-28 border-b border-white/10" data-testid="give-instantly">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">Give Instantly</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight tracking-tight max-w-3xl">
              Scan and give in <em className="italic">seconds.</em>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-6 max-w-2xl text-lg">
              Prefer your favorite app? Scan a code below with your phone to send a gift directly to
              The Tandem T Foundation.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: "venmo", label: "Venmo", handle: "@TandemTFoundation", img: "/pay/venmo.png" },
              { key: "cashapp", label: "Cash App", handle: "$TandemTFoundation", img: "/pay/cashapp.png" },
              { key: "zelle", label: "Zelle", handle: "tandemtfoundation", img: "/pay/zelle.png" },
              { key: "paypal", label: "PayPal", handle: "Tandem T Foundation", img: "/pay/paypal.png" },
            ].map((p, i) => (
              <Reveal key={p.key} delay={i * 0.06}>
                <div
                  data-testid={`give-qr-${p.key}`}
                  className="border border-white/12 bg-white/[0.02] p-6 flex flex-col items-center text-center h-full"
                >
                  <div className="w-full aspect-square bg-white rounded-xl flex items-center justify-center overflow-hidden p-3">
                    <img
                      src={p.img}
                      alt={`${p.label} donation QR code for The Tandem T Foundation`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <p className="font-serif text-white text-2xl font-light mt-6">{p.label}</p>
                  <p className="text-white/55 font-light text-sm mt-1">{p.handle}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="text-xs text-white/40 font-light mt-8 max-w-2xl">
              Gifts sent through these apps support the charitable mission of Tandem T Foundation. For a
              tax-deductible receipt, please include your email in the payment note.
            </p>
          </Reveal>
        </div>
      </section>


      {/* Ways to Give */}
      <section className="py-20 md:py-28" data-testid="ways-to-give">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-white/55">More Ways to Give</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight tracking-tight max-w-3xl">
              Every kind of generosity <em className="italic">builds something.</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {waysToGive.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.05} className="bg-black">
                <div className="p-8 min-h-[220px] flex flex-col">
                  <w.icon size={22} className="text-white" />
                  <p className="font-serif text-white text-2xl font-light mt-6">{w.title}</p>
                  <p className="text-white/60 font-light text-sm mt-3 leading-relaxed">{w.desc}</p>
                  {w.link && (
                    <a
                      href={w.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="paypal-giving-fund-link"
                      className="group inline-flex items-center gap-2 overline text-white mt-auto pt-6 hover:opacity-80 transition-opacity"
                    >
                      {w.cta}
                      <ArrowUpRight size={14} className="hover-arrow" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
